/**
 * First run.
 *
 * The moment sharing is switched on, two histories that have never met have to
 * become one. Neither is disposable: one of them is the plan somebody sat down
 * and made.
 *
 * So nothing is uploaded or overwritten until the person on THAT device has
 * been asked, once, for that week. There is no default path that discards a
 * plan.
 */

import {
  loadPlan, loadSyncState, savePlan, saveSyncState, withItemMeta, type StoredPlan,
} from '../store/db.js';
import type { Store } from '../../domain/types.js';
import { mergeItems, type SyncItem } from './merge.js';
import { itemsToPlan, planToItems } from './planItems.js';
import { fetchRemote, householdId, pushItems } from './client.js';
import { deviceId } from './device.js';
import { sharingActive } from './engine.js';

export type AdoptChoice = 'local' | 'remote' | 'merge';

export interface AdoptQuestion {
  weekStart: string;
  /** What is actually on this phone, for the prompt to describe honestly. */
  localSummary: string;
  remoteSummary: string;
}

/**
 * Auto-seeded staples and carried-forward pantry answers are not "a plan
 * somebody made" — every fresh week has them. Asking about a week whose only
 * content is the standing staples would train people to dismiss the question
 * that actually matters.
 */
function hasRealContent(plan: StoredPlan | null | undefined): boolean {
  if (!plan) return false;
  return plan.dinners.length > 0
    || plan.lunches.length > 0
    || plan.manual.length > 0
    || Object.values(plan.ticked).some(Boolean);
}

function summarise(plan: StoredPlan | null): string {
  if (!plan) return 'nothing yet';
  const bits: string[] = [];
  if (plan.dinners.length > 0) bits.push(`${plan.dinners.length} dinner${plan.dinners.length === 1 ? '' : 's'}`);
  if (plan.lunches.length > 0) bits.push(`${plan.lunches.length} lunch${plan.lunches.length === 1 ? '' : 'es'}`);
  if (plan.manual.length > 0) bits.push(`${plan.manual.length} extra item${plan.manual.length === 1 ? '' : 's'}`);
  const ticks = Object.values(plan.ticked).filter(Boolean).length;
  if (ticks > 0) bits.push(`${ticks} ticked off`);
  return bits.length > 0 ? bits.join(', ') : 'nothing yet';
}

/**
 * Is there a genuine conflict for this week on this device?
 *
 * Only when BOTH sides hold a real plan. One side alone is not a conflict — it
 * is adopted silently, so a device holding three weeks against an account
 * holding one is asked once, not three times.
 */
export async function adoptionNeeded(weekStart: string): Promise<AdoptQuestion | null> {
  if (!(await sharingActive())) return null;

  const state = await loadSyncState(weekStart);
  if (state.adopted === true || state.lastSyncedAt !== null) return null;

  const household = await householdId();
  if (!household) return null;

  const local = (await loadPlan(weekStart)) ?? null;
  if (!hasRealContent(local)) return null;

  const remote = await fetchRemote(household, weekStart);
  const remotePlan = remote.plan.length > 0
    ? itemsToPlan(weekStart, local?.store ?? 'aldi', remote.plan)
    : null;
  if (!hasRealContent(remotePlan)) return null;

  return {
    weekStart,
    localSummary: summarise(local),
    remoteSummary: summarise(remotePlan),
  };
}

/**
 * Apply the answer.
 *
 * "Keep this phone's" re-stamps every local item to now so it wins the merge
 * outright, and tombstones anything the account held that this phone does not.
 * Re-stamping rather than deleting the remote rows means the OTHER device
 * converges on the same answer through the ordinary merge, with no special
 * case to get wrong.
 */
export async function applyAdoption(
  weekStart: string,
  choice: AdoptChoice,
  fallbackStore: Store,
): Promise<StoredPlan | null> {
  const household = await householdId();
  if (!household) return null;

  const local = (await loadPlan(weekStart)) ?? null;
  const localItems = local ? planToItems(withItemMeta(local)) : [];
  const remote = await fetchRemote(household, weekStart);

  let winning: SyncItem[];
  switch (choice) {
    case 'merge':
      winning = mergeItems(localItems, remote.plan);
      break;

    case 'remote':
      winning = remote.plan;
      break;

    case 'local': {
      const now = new Date().toISOString();
      const by = deviceId();
      const localKeys = new Set(localItems.map((i) => i.key));
      const buried: SyncItem[] = remote.plan
        .filter((i) => !localKeys.has(i.key) && i.value !== null)
        .map((i) => ({ key: i.key, value: null, meta: { updatedAt: now, updatedBy: by, deleted: true } }));
      winning = [
        ...localItems.map((i) => ({ ...i, meta: { ...i.meta, updatedAt: now, updatedBy: by } })),
        ...buried,
      ];
      break;
    }
  }

  const plan = itemsToPlan(weekStart, local?.store ?? fallbackStore, winning);
  await savePlan(plan);

  // Push the decision immediately. Leaving it to the next ordinary sync would
  // mean the other phone could push its own version first and win a question
  // that has already been answered.
  await pushItems(household, weekStart, winning);
  await saveSyncState({ weekStart, lastSyncedAt: new Date().toISOString(), adopted: true });

  return plan;
}

/** Record that a week needs no question — nothing to reconcile. */
export async function markAdopted(weekStart: string): Promise<void> {
  const state = await loadSyncState(weekStart);
  await saveSyncState({ ...state, adopted: true });
}
